/** Bitcoin / Solana-style base58 (no 0, O, I, l). */

const ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.trim().replace(/^0x/i, "").toLowerCase();
  if (clean.length % 2 !== 0 || !/^[0-9a-f]*$/.test(clean)) {
    throw new Error("invalid hex");
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function encodeBase58(bytes: Uint8Array): string {
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;

  const digits = [0];
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }

  let out = "1".repeat(zeros);
  for (let i = digits.length - 1; i >= 0; i--) {
    out += ALPHABET[digits[i]!]!;
  }
  return out;
}

/** True when the string is a full 32-byte Arch id (64 hex chars). */
export function looksLikeHexId(id: string): boolean {
  const clean = id.trim().replace(/^0x/i, "");
  // Reject short/long hex — encoding a truncated digest yields plausible but
  // explorer-404 base58 that looks like a real txid.
  return clean.length === 64 && /^[0-9a-fA-F]+$/.test(clean);
}

/**
 * Arch explorer user-facing ids are base58. API often returns hex —
 * convert when needed, pass through if already base58.
 */
export function toBase58Id(id: string): string {
  if (!looksLikeHexId(id)) return id;
  return encodeBase58(hexToBytes(id.trim().replace(/^0x/i, "")));
}

/**
 * Short shore label from a validated 64-hex txid. Returns null when the
 * id is missing/invalid or would render as a blank chip.
 */
export function shortTxLabel(txid: string): string | null {
  if (!looksLikeHexId(txid)) return null;
  const full = toBase58Id(txid);
  if (!full || full.length < 4) return null;
  return full.slice(0, 7);
}
